import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useStoryStore } from '../stores/storyStore';
import CreateStoryModal from '../components/CreateStoryModal';

const { FiPlus, FiEdit, FiCopy, FiTrash2, FiShare2, FiEye, FiFileText } = FiIcons;

const TeacherDashboard = () => {
  const { stories, deleteStory, duplicateStory, updateStory } = useStoryStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleDelete = (storyId) => {
    deleteStory(storyId);
    setDeleteConfirm(null);
  };

  const handleDuplicate = (storyId) => {
    duplicateStory(storyId);
  };

  const handleToggleStatus = (story) => {
    const newStatus = story.status === 'published' ? 'draft' : 'published';
    updateStory(story.id, { status: newStatus });
  };

  const getShareUrl = (shareId) => {
    return `${window.location.origin}/#/story/${shareId}`;
  };

  const copyShareLink = async (shareId) => {
    const url = getShareUrl(shareId);
    try {
      await navigator.clipboard.writeText(url);
      alert('Link in die Zwischenablage kopiert!');
    } catch (err) {
      console.error('Kopieren fehlgeschlagen: ', err);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold font-rubik" style={{ color: '#2b6777' }}>
              Meine Geschichten
            </h1>
            <p className="mt-2 font-rubik" style={{ color: '#2b6777' }}>
              Erstellen und verwalten Sie Ihre interaktiven Entscheidungsgeschichten
            </p>
          </div>
          <motion.button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors text-white font-rubik"
            style={{ backgroundColor: '#c8d8e4' }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            <span>Neue Geschichte</span>
          </motion.button>
        </div>

        {/* Stories Grid */}
        {stories.length === 0 ? (
          <motion.div
            className="text-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <SafeIcon icon={FiFileText} className="w-16 h-16 mx-auto mb-4" style={{ color: '#9ca3af' }} />
            <h3 className="text-xl font-medium mb-2 font-rubik" style={{ color: '#2b6777' }}>
              Noch keine Geschichten
            </h3>
            <p className="mb-6 font-rubik" style={{ color: '#2b6777' }}>
              Erstellen Sie Ihre erste interaktive Entscheidungsgeschichte, um zu beginnen
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors text-white font-rubik"
              style={{ backgroundColor: '#c8d8e4' }}
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Geschichte erstellen</span>
            </button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stories.map((story, index) => (
              <motion.div
                key={story.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow border border-gray-200"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold mb-2 font-rubik" style={{ color: '#2b6777' }}>
                        {story.title}
                      </h3>
                      {story.description && (
                        <p className="text-sm mb-3 line-clamp-2 font-rubik" style={{ color: '#2b6777' }}>
                          {story.description}
                        </p>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium font-rubik ${
                        story.status === 'published'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {story.status === 'published' ? 'Veröffentlicht' : 'Entwurf'}
                    </span>
                  </div>

                  <div className="text-sm mb-4 font-rubik" style={{ color: '#2b6777' }}>
                    Erstellt: {format(new Date(story.createdAt), 'dd.MM.yyyy')}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/editor/${story.id}`}
                        className="p-2 rounded transition-colors"
                        style={{ color: '#2b6777' }}
                        title="Bearbeiten"
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDuplicate(story.id)}
                        className="p-2 rounded transition-colors"
                        style={{ color: '#2b6777' }}
                        title="Duplizieren"
                      >
                        <SafeIcon icon={FiCopy} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(story.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Löschen"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex items-center space-x-2">
                      {story.status === 'published' && (
                        <>
                          <button
                            onClick={() => copyShareLink(story.shareId)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Link kopieren"
                          >
                            <SafeIcon icon={FiShare2} className="w-4 h-4" />
                          </button>
                          <Link
                            to={`/story/${story.shareId}`}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                            title="Vorschau"
                          >
                            <SafeIcon icon={FiEye} className="w-4 h-4" />
                          </Link>
                        </>
                      )}
                      <button
                        onClick={() => handleToggleStatus(story)}
                        className={`px-3 py-1 text-xs rounded transition-colors font-rubik ${
                          story.status === 'published'
                            ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            : 'text-white'
                        }`}
                        style={story.status === 'draft' ? { backgroundColor: '#c8d8e4' } : {}}
                      >
                        {story.status === 'published' ? 'Verstecken' : 'Veröffentlichen'}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Create Story Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateStoryModal onClose={() => setShowCreateModal(false)} />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-lg p-6 w-full max-w-md"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3 className="text-lg font-semibold mb-4 font-rubik" style={{ color: '#2b6777' }}>
                Geschichte löschen
              </h3>
              <p className="mb-6 font-rubik" style={{ color: '#2b6777' }}>
                Sind Sie sicher, dass Sie diese Geschichte löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 transition-colors font-rubik"
                  style={{ color: '#2b6777' }}
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors font-rubik"
                >
                  Löschen
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeacherDashboard;